import { alienInvasionFaq } from './faq';
import { alienInvasionGlossary } from './glossary';
import { alienInvasionMistakes } from './mistakes';
import { alienInvasionQuickReference } from './quickReference';
import {
  alienInvasionSectionById,
  getAlienInvasionSections,
} from './sectionRegistry';
import { getAlienInvasionGlobalSection } from './beacon';
import type {
  AlienFaqEntry,
  AlienGlossaryEntry,
  AlienGlobalSection,
  AlienMistakeEntry,
  AlienQuickReference,
  AlienRuleSection,
  AlienScenarioId,
} from './types';
import { isAlienScenarioId } from './expansion';

/** Renderable block for Alien Invasion screens (adapter from AlienRuleSection fields). */
export type AlienInvasionContentBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'warning'; text: string }
  | { type: 'tip'; text: string }
  | { type: 'step-list'; steps: string[] }
  | { type: 'bullet-list'; items: string[] };

export type AlienInvasionViewKind =
  | 'rule'
  | 'faq'
  | 'mistakes'
  | 'glossary'
  | 'quick'
  | 'reference';

/**
 * Unified view model for Alien Invasion section screen.
 * Rule content comes 1:1 from AlienRuleSection; hub kinds aggregate existing FAQ/etc.
 * Global reference (Beacon) uses scenarioId 'all'.
 */
export type AlienInvasionSectionView = {
  id: string;
  scenarioId: AlienScenarioId | 'all';
  kind: AlienInvasionViewKind;
  title: string;
  content: AlienInvasionContentBlock[];
  relatedSectionIds: string[];
};

export function virtualSectionId(
  scenarioId: AlienScenarioId,
  kind: Exclude<AlienInvasionViewKind, 'rule' | 'reference'>
): string {
  if (kind === 'quick') return `ai:${scenarioId}:quick-reference`;
  return `ai:${scenarioId}:${kind}`;
}

export function parseVirtualSectionId(
  sectionId: string
):
  | {
      scenarioId: AlienScenarioId;
      kind: Exclude<AlienInvasionViewKind, 'rule' | 'reference'>;
    }
  | null {
  const match =
    /^ai:(invasion|outbreak|blackout|monolith):(faq|mistakes|glossary|quick-reference)$/.exec(
      sectionId
    );
  if (!match) return null;
  const scenarioId = match[1] as AlienScenarioId;
  const raw = match[2];
  const kind =
    raw === 'quick-reference'
      ? ('quick' as const)
      : (raw as 'faq' | 'mistakes' | 'glossary');
  return { scenarioId, kind };
}

function contentFromFields(section: {
  summary?: string;
  paragraphs?: string[];
  bullets?: string[];
  steps?: string[];
  warnings?: string[];
  examples?: string[];
  bulletHeading?: string;
}): AlienInvasionContentBlock[] {
  const blocks: AlienInvasionContentBlock[] = [];

  if (section.summary) {
    blocks.push({ type: 'paragraph', text: section.summary });
  }
  for (const paragraph of section.paragraphs ?? []) {
    if (section.summary && paragraph === section.summary) continue;
    blocks.push({ type: 'paragraph', text: paragraph });
  }
  if (section.bullets?.length) {
    if (section.bulletHeading) {
      blocks.push({ type: 'heading', text: section.bulletHeading });
    }
    blocks.push({ type: 'bullet-list', items: section.bullets });
  }
  if (section.steps?.length) {
    blocks.push({ type: 'step-list', steps: section.steps });
  }
  for (const warning of section.warnings ?? []) {
    blocks.push({ type: 'warning', text: warning });
  }
  for (const example of section.examples ?? []) {
    blocks.push({ type: 'tip', text: example });
  }

  return blocks;
}

export function ruleSectionToBlocks(
  section: AlienRuleSection
): AlienInvasionContentBlock[] {
  return contentFromFields(section);
}

export function globalSectionToBlocks(
  section: AlienGlobalSection
): AlienInvasionContentBlock[] {
  return contentFromFields({
    ...section,
    bulletHeading:
      section.id === 'ai:reference:beacon'
        ? 'Akcja zaawansowanego budynku'
        : undefined,
  });
}

export function filterFaqForScenario(
  scenarioId: AlienScenarioId
): AlienFaqEntry[] {
  return alienInvasionFaq.filter(
    (entry) => entry.scenarioId === 'all' || entry.scenarioId === scenarioId
  );
}

export function filterMistakesForScenario(
  scenarioId: AlienScenarioId
): AlienMistakeEntry[] {
  return alienInvasionMistakes.filter(
    (entry) => entry.scenarioId === scenarioId
  );
}

export function filterGlossaryForScenario(
  scenarioId: AlienScenarioId
): AlienGlossaryEntry[] {
  return alienInvasionGlossary.filter((entry) =>
    entry.scenarioIds.includes(scenarioId)
  );
}

export function getQuickReferenceForScenario(
  scenarioId: AlienScenarioId
): AlienQuickReference | undefined {
  return alienInvasionQuickReference.find(
    (entry) => entry.scenarioId === scenarioId
  );
}

function faqToView(scenarioId: AlienScenarioId): AlienInvasionSectionView {
  const entries = filterFaqForScenario(scenarioId);
  const content: AlienInvasionContentBlock[] = [];
  for (const entry of entries) {
    content.push({ type: 'heading', text: entry.question });
    content.push({ type: 'paragraph', text: entry.answer });
  }
  return {
    id: virtualSectionId(scenarioId, 'faq'),
    scenarioId,
    kind: 'faq',
    title: 'FAQ',
    content,
    relatedSectionIds: entries.flatMap((e) => e.relatedSectionIds ?? []),
  };
}

function mistakesToView(
  scenarioId: AlienScenarioId
): AlienInvasionSectionView {
  const entries = filterMistakesForScenario(scenarioId);
  const content: AlienInvasionContentBlock[] = [];
  for (const entry of entries) {
    content.push({ type: 'heading', text: entry.title });
    content.push({ type: 'paragraph', text: entry.explanation });
  }
  return {
    id: virtualSectionId(scenarioId, 'mistakes'),
    scenarioId,
    kind: 'mistakes',
    title: 'Typowe błędy',
    content,
    relatedSectionIds: entries.flatMap((e) => e.relatedSectionIds ?? []),
  };
}

function glossaryToView(
  scenarioId: AlienScenarioId
): AlienInvasionSectionView {
  const entries = [...filterGlossaryForScenario(scenarioId)].sort((a, b) =>
    a.term.localeCompare(b.term, 'pl')
  );
  const content: AlienInvasionContentBlock[] = [];
  for (const entry of entries) {
    content.push({ type: 'heading', text: entry.term });
    content.push({ type: 'paragraph', text: entry.definition });
  }
  return {
    id: virtualSectionId(scenarioId, 'glossary'),
    scenarioId,
    kind: 'glossary',
    title: 'Słownik',
    content,
    relatedSectionIds: entries.flatMap((e) => e.relatedSectionIds ?? []),
  };
}

function quickToView(
  scenarioId: AlienScenarioId
): AlienInvasionSectionView | undefined {
  const ref = getQuickReferenceForScenario(scenarioId);
  if (!ref) return undefined;
  return {
    id: virtualSectionId(scenarioId, 'quick'),
    scenarioId,
    kind: 'quick',
    title: ref.title,
    content: [
      { type: 'heading', text: 'Przygotowanie' },
      { type: 'step-list', steps: ref.setupChecklist },
      { type: 'heading', text: 'Tura / przebieg' },
      { type: 'step-list', steps: ref.turnChecklist },
      { type: 'heading', text: 'Zwycięstwo' },
      { type: 'step-list', steps: ref.victoryChecklist },
    ],
    relatedSectionIds: [],
  };
}

export function getAlienInvasionSectionsForScenario(
  scenarioId: string
): AlienRuleSection[] {
  if (!isAlienScenarioId(scenarioId)) return [];
  return getAlienInvasionSections(scenarioId);
}

export function getAlienInvasionHubEntries(
  scenarioId: AlienScenarioId
): Array<{ id: string; title: string; kind: AlienInvasionViewKind }> {
  return [
    {
      id: virtualSectionId(scenarioId, 'quick'),
      title: 'Szybka ściąga',
      kind: 'quick',
    },
    { id: virtualSectionId(scenarioId, 'faq'), title: 'FAQ', kind: 'faq' },
    {
      id: virtualSectionId(scenarioId, 'mistakes'),
      title: 'Typowe błędy',
      kind: 'mistakes',
    },
    {
      id: virtualSectionId(scenarioId, 'glossary'),
      title: 'Słownik',
      kind: 'glossary',
    },
    {
      id: 'ai:reference:beacon',
      title: 'Beacon (promo)',
      kind: 'reference',
    },
  ];
}

export function sectionBelongsToActiveScenario(
  sectionId: string,
  activeScenarioId: AlienScenarioId | null
): boolean {
  if (sectionId === 'ai:reference:beacon') {
    return true; // global — ok whenever expansion is on
  }
  const virtual = parseVirtualSectionId(sectionId);
  if (virtual) {
    return virtual.scenarioId === activeScenarioId;
  }
  const section = alienInvasionSectionById[sectionId];
  if (!section) return false;
  return section.scenarioId === activeScenarioId;
}

export function getAlienInvasionSection(
  sectionId: string
): AlienInvasionSectionView | undefined {
  if (sectionId === 'ai:reference:beacon') {
    const global = getAlienInvasionGlobalSection(sectionId);
    if (!global) return undefined;
    return {
      id: global.id,
      scenarioId: 'all',
      kind: 'reference',
      title: global.title,
      content: globalSectionToBlocks(global),
      relatedSectionIds: global.relatedSectionIds ?? [],
    };
  }

  const virtual = parseVirtualSectionId(sectionId);
  if (virtual) {
    if (virtual.kind === 'faq') return faqToView(virtual.scenarioId);
    if (virtual.kind === 'mistakes') return mistakesToView(virtual.scenarioId);
    if (virtual.kind === 'glossary') return glossaryToView(virtual.scenarioId);
    return quickToView(virtual.scenarioId);
  }

  const section = alienInvasionSectionById[sectionId];
  if (!section) return undefined;

  return {
    id: section.id,
    scenarioId: section.scenarioId,
    kind: 'rule',
    title: section.title,
    content: ruleSectionToBlocks(section),
    relatedSectionIds: section.relatedSectionIds ?? [],
  };
}
